<template>
    <div class="space-y-4">
        <!-- Stats Overview -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
                :title="t('dashboard.totalStudents')"
                :value="data?.stats?.totalStudents || 0"
                :icon="GraduationCap"
                color="blue"
            />
            <StatCard
                :title="t('dashboard.totalTeachers')"
                :value="data?.stats?.totalTeachers || 0"
                :icon="User"
                color="green"
            />
            <StatCard
                :title="t('dashboard.totalClasses')"
                :value="data?.stats?.totalClasses || 0"
                :icon="Home"
                color="purple"
            />
            <StatCard
                :title="t('dashboard.activeEnrollments')"
                :value="data?.stats?.activeEnrollments || 0"
                :icon="CheckCircle"
                color="yellow"
            />
        </div>

        <!-- Recent Enrollments and Alerts -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <!-- Recent Student Class Enrollments -->
            <div class="bg-white dark:bg-gray-800 shadow rounded-lg">
                <div class="px-4 py-4 sm:p-5">
                    <h3
                        class="text-base leading-6 font-medium text-gray-900 dark:text-white mb-3"
                    >
                        {{ t("dashboard.recentActivity") }}
                    </h3>
                    <div class="space-y-2">
                        <div
                            v-for="enrollment in data?.recentEnrollments || []"
                            :key="enrollment.id"
                            class="flex items-center justify-between p-2.5 bg-gray-50 dark:bg-gray-700 rounded-lg"
                        >
                            <div class="flex items-center">
                                <div
                                    class="flex-shrink-0 h-10 w-10 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center"
                                >
                                    <User
                                        class="h-5 w-5 text-primary-600 dark:text-primary-300"
                                    />
                                </div>
                                <div class="ml-3">
                                    <p
                                        class="text-sm font-medium text-gray-900 dark:text-white"
                                    >
                                        {{ enrollment.studentName }}
                                    </p>
                                    <p
                                        class="text-sm text-gray-500 dark:text-gray-400"
                                    >
                                        {{ enrollment.className }}
                                    </p>
                                </div>
                            </div>
                            <div class="text-right">
                                <span
                                    :class="[
                                        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                                        getStatusClasses(enrollment.status),
                                    ]"
                                >
                                    {{ enrollment.status }}
                                </span>
                                <p
                                    class="text-xs text-gray-500 dark:text-gray-400 mt-1"
                                >
                                    {{ formatDate(enrollment.enrollmentDate) }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- System Alerts -->
            <AlertsList :alerts="data?.alerts || []" />
        </div>

        <!-- Quick Actions -->
        <QuickActions :actions="data?.quickActions || []" />

        <!-- AI Insight -->
        <AiInsightCard v-if="authStore.hasPermission('ai-assistant.use')" />
    </div>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import {
    AdministratorDashboardData,
    RecentEnrollment,
} from "@/types/dashboard";
import StatCard from "./StatCard.vue";
import QuickActions from "./QuickActions.vue";
import AlertsList from "./AlertsList.vue";
import AiInsightCard from "./AiInsightCard.vue";
import { GraduationCap, User, Home, CheckCircle } from "@lucide/vue";
import { useAuthStore } from "@/stores/authStore";

interface Props {
    data: AdministratorDashboardData;
}

defineProps<Props>();
const { t, locale } = useI18n();
const authStore = useAuthStore();

const getStatusClasses = (status: string) => {
    const classes = {
        active: "bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300",
        pending:
            "bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300",
        inactive:
            "bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300",
    };
    return classes[status as keyof typeof classes] || classes.pending;
};

const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(locale.value, {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
};
</script>
